import React, { useState, useEffect } from 'react';
import getApiUrl from '../apiUrl';
import apiFetch from '../apiFetch';

const FileUpload = ({ onDataProcessed, onError, onLoading, onFetchLive, liveProgress, darkMode = false }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  // Live fetch state is owned by App (it streams sites + survives view switches).
  const [previewData, setPreviewData] = useState(null);
  const [clientName, setClientName] = useState('');
  const [customFilename, setCustomFilename] = useState('');

  // Live clients pull real-time inverter data from an external portal (VCOM /
  // Unity) instead of an upload. Listed first in the dropdown.
  const LIVE_CLIENTS = ['belectric', 'metlen', 'ameresco'];
  const clientOptions = ['belectric', 'metlen', 'ameresco', 'cleanleaf', 'standard solar', 'puresky'];
  const isLiveClient = LIVE_CLIENTS.includes(clientName);
  // Belectric supports BOTH live data (VCOM) and uploading a VCOM CSV export, so
  // its upload area shows alongside the live-fetch panel. Other live clients are
  // live-only for now.
  const supportsUpload = !isLiveClient || clientName === 'belectric';
  const acceptsCsv = clientName === 'belectric';
  const acceptExt = acceptsCsv ? '.xlsx,.csv' : '.xlsx';
  const isAcceptedFileName = (name) =>
    (name || '').toLowerCase().endsWith('.xlsx') || (acceptsCsv && (name || '').toLowerCase().endsWith('.csv'));
  
  // Update custom filename when file or client changes
  useEffect(() => {
    if (selectedFile) {
      if (clientName) {
        setCustomFilename(`${clientName} - ${selectedFile.name}`);
      } else {
        setCustomFilename(selectedFile.name);
      }
    }
  }, [selectedFile, clientName]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      onError(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      onError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      onError('Please select a file first');
      return;
    }

    if (!isAcceptedFileName(selectedFile.name)) {
      onError(acceptsCsv ? 'Please upload an Excel (.xlsx) or Belectric CSV (.csv) file' : 'Please upload an Excel file (.xlsx)');
      return;
    }

    setIsUploading(true);
    onLoading(true);
    onError(null);
    setPreviewData(null); // Clear preview when analyzing

    if (!clientName) {
      onError('Please select a client name');
      setIsUploading(false);
      onLoading(false);
      return;
    }

    if (!customFilename || customFilename.trim() === '') {
      onError('Please enter a file name');
      setIsUploading(false);
      onLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('client_name', clientName);
    formData.append('display_filename', customFilename.trim());

    try {
      console.log('Uploading file:', selectedFile.name, 'for client:', clientName);
      // Use local backend for development, or cloud URL for production
      const API_URL = getApiUrl();
      console.log('API URL:', API_URL);
      
      const response = await apiFetch(`/upload`, {
        method: 'POST',
        body: formData,
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}. ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Response result:', result);

      if (result.success) {
        onDataProcessed(result);
      } else {
        onError(result.error || 'Failed to process file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Unknown error';
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        onError(`Network error: Cannot connect to backend server. Please check your internet connection and try again. If the problem persists, contact support. (API URL: ${getApiUrl()})`);
      } else {
        onError('Error: ' + errorMessage);
      }
    } finally {
      setIsUploading(false);
      onLoading(false);
    }
  };

  // Delegate to App, which streams sites in progressively and survives the
  // switch to the dashboard view (this component unmounts once data loads).
  const handleFetchLive = () => {
    setPreviewData(null);
    onError(null);
    if (onFetchLive) onFetchLive(clientName);
  };
  const liveBusy = !!liveProgress && liveProgress.client === clientName;

  const handlePreview = async () => {
    console.log('Preview button clicked');
    
    if (!selectedFile) {
      console.log('No file selected');
      onError('Please select a file first');
      return;
    }

    if (!isAcceptedFileName(selectedFile.name)) {
      console.log('Invalid file type:', selectedFile.name);
      onError(acceptsCsv ? 'Please upload an Excel (.xlsx) or Belectric CSV (.csv) file' : 'Please upload an Excel file (.xlsx)');
      return;
    }

    console.log('Starting preview for file:', selectedFile.name);
    setIsPreviewing(true);
    onError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      console.log('Sending preview request...');
      // Use local backend for development, or cloud URL for production
      const API_URL = getApiUrl();
      console.log('API URL:', API_URL);
      
      const response = await apiFetch(`/preview`, {
        method: 'POST',
        body: formData,
      });

      console.log('Preview response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Preview response error:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}. ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Preview result:', result);

      if (result.success) {
        console.log('Preview successful');
        setPreviewData(result);
      } else {
        console.log('Preview failed:', result.error);
        onError(result.error || 'Failed to preview file');
      }
    } catch (error) {
      console.error('Preview error:', error);
      const errorMessage = error.message || 'Unknown error';
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        onError(`Network error: Cannot connect to backend server. Please check your internet connection and try again. If the problem persists, contact support. (API URL: ${getApiUrl()})`);
      } else {
        onError('Preview error: ' + errorMessage);
      }
    } finally {
      console.log('Preview completed, setting isPreviewing to false');
      setIsPreviewing(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setPreviewData(null);
    onError(null);
  };

  const handleClearPreview = () => {
    setPreviewData(null);
  };

  return (
    <div className="w-full mb-8">
      {/* Client Name and Filename Selection */}
      <div className={`mb-4 rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Select Client Name
            </label>
            <select
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
            >
              <option value="">-- Select Client --</option>
              {clientOptions.map((client) => (
                <option key={client} value={client}>
                  {client.charAt(0).toUpperCase() + client.slice(1)}{LIVE_CLIENTS.includes(client) ? ' (Live)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              File Name (editable)
            </label>
            <input
              type="text"
              value={customFilename}
              onChange={(e) => setCustomFilename(e.target.value)}
              placeholder="Enter file name..."
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
              disabled={!selectedFile}
            />
            {selectedFile && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Original: {selectedFile.name}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Live client (e.g. Belectric): fetch real-time data from VCOM instead of uploading a file */}
      {isLiveClient && (
        <div className={`rounded-lg shadow-md p-6 border-l-4 border-green-500 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
              <svg className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Live Data — {clientName.charAt(0).toUpperCase() + clientName.slice(1)}
              </h3>
              <p className={`text-sm mt-1 mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                No file needed. This pulls today's real-time inverter data straight from the VCOM portal
                and runs the same health-ratio analysis. This can take ~15–30 seconds.
              </p>
              <button
                onClick={handleFetchLive}
                disabled={liveBusy}
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors text-sm ${
                  liveBusy
                    ? 'bg-gray-300 text-gray-500 cursor-wait'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {liveBusy ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{liveProgress?.total ? `Loading ${liveProgress.loaded}/${liveProgress.total} sites…` : 'Fetching…'}</span>
                  </div>
                ) : (
                  'Fetch Live Data'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File upload — always for standard clients; also offered for Belectric
          (VCOM CSV export) in addition to its live-data panel above. */}
      {supportsUpload && (
      <div>
      {isLiveClient && (
        <div className={`flex items-center gap-3 my-4`}>
          <div className={`flex-1 h-px ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`} />
          <span className={`text-xs font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Or upload a file
          </span>
          <div className={`flex-1 h-px ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`} />
        </div>
      )}
      <div className="flex items-center gap-4">
        {/* Upload Area - Compact */}
        <div className="flex-1">
          <div
            className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              dragActive
                ? 'border-green-400 bg-green-50'
                : darkMode 
                  ? 'border-gray-600 hover:border-green-400 bg-gray-700'
                  : 'border-gray-300 hover:border-green-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept={acceptExt}
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="space-y-2">
              <div className={`mx-auto w-8 h-8 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              
              <div>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Upload Inverter Data
                </h3>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Drag & drop or click to browse
                </p>
              </div>
              
              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <p>{acceptsCsv ? '.csv or .xlsx files' : '.xlsx files only'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Right Side */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handlePreview}
            disabled={!selectedFile || isPreviewing || isUploading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
              !selectedFile || isPreviewing || isUploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isPreviewing ? (
              <div className="flex items-center space-x-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                <span>Previewing...</span>
              </div>
            ) : (
              'Preview File'
            )}
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isUploading || isPreviewing}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
              !selectedFile || isUploading || isPreviewing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isUploading ? (
              <div className="flex items-center space-x-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                <span>Processing...</span>
              </div>
            ) : (
              'Analyze Data'
            )}
          </button>
          
          {selectedFile && (
            <button
              onClick={handleClearFile}
              disabled={isUploading || isPreviewing}
              className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                isUploading || isPreviewing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Clear File
            </button>
          )}
        </div>
      </div>
      </div>
      )}

      {/* Selected File Display */}
      {supportsUpload && selectedFile && (
        <div className={`mt-4 p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-green-700' : 'bg-white border-green-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 text-green-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <button
              onClick={handleClearFile}
              className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Preview Data Display */}
      {previewData && (
        <div className={`mt-6 rounded-lg shadow-lg p-6 border ${darkMode ? 'bg-gray-700 border-green-700' : 'bg-white border-green-200'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-green-400' : 'text-green-800'}`}>File Preview</h3>
            <button
              onClick={handleClearPreview}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Clear Preview
            </button>
          </div>
          <div className="space-y-4">
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Total Sites Found:</strong> {previewData.total_sites}
            </div>
            
            {previewData.sites_preview.map((site, index) => (
              <div key={index} className={`rounded-lg p-4 border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Site {index + 1}: {site.site_name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong>Data Shape:</strong> {site.data_shape[0]} rows × {site.data_shape[1]} columns
                  </div>
                  <div>
                    <strong>Inverter Columns:</strong> {site.inverter_columns.length}
                  </div>
                  {site.stored_config_found && site.stored_config_info && (
                    <div className="md:col-span-2">
                      <strong className="text-green-700">✓ Stored Configuration Found</strong>
                      <div className="mt-1 text-xs text-gray-600">
                        <p>Site: {site.stored_config_info.site_name}</p>
                        <p>Inverters: {site.stored_config_info.inverter_count}</p>
                      </div>
                    </div>
                  )}
                  {!site.stored_config_found && (
                    <div className="md:col-span-2">
                      <strong className="text-yellow-700">⚠ No Stored Configuration</strong>
                      <div className="mt-1 text-xs text-gray-600">
                        Will extract capacities from column names
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <strong>Inverter Columns:</strong>
                    {site.inverter_columns.length > 0 ? (
                      <div className="mt-1 space-y-2">
                        {site.inverter_columns.map((invCol, i) => (
                          <div key={i} className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                            <div className="font-medium text-blue-800">
                              Column: {invCol.column_name || `Column ${invCol.column_index + 1}`}
                            </div>
                            {invCol.mapped_to && (
                              <div className="text-green-700 mt-1">
                                → Maps to: <strong>{invCol.mapped_to}</strong>
                                {invCol.will_use_capacity_kw && (
                                  <span> (Max Capacity: {invCol.will_use_capacity_kw} kW)</span>
                                )}
                              </div>
                            )}
                            {invCol.min_value !== undefined && (
                              <div className="text-gray-600 mt-1">
                                Values: {invCol.min_value.toFixed(2)} - {invCol.max_value.toFixed(2)} kW
                                {invCol.data_points && ` (${invCol.data_points} data points)`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-gray-500 italic">
                        No inverter columns detected. {site.stored_config_found 
                          ? "Make sure numeric power columns exist." 
                          : "Columns need 'Inv' or 'PVI' and 'Active Power' in name, or use stored configuration."}
                      </div>
                    )}
                  </div>
                </div>
                
                {site.sample_data.length > 0 && (
                  <div className="mt-3">
                    <strong>Sample Data (First 3 rows):</strong>
                    <div className="mt-2 overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            {Object.keys(site.sample_data[0]).map((key, i) => (
                              <th key={i} className="px-2 py-1 text-left font-medium text-gray-700">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {site.sample_data.map((row, i) => (
                            <tr key={i} className="border-t">
                              {Object.values(row).map((value, j) => (
                                <td key={j} className="px-2 py-1 text-gray-600">
                                  {value !== null && value !== undefined ? String(value) : '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default FileUpload;
